import { Capacitor } from '@capacitor/core';
import React, { useEffect, useCallback } from 'react';
import { getAll, getValue, getRemoteConfig, fetchAndActivate } from 'firebase/remote-config';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useNetworkStatus } from 'src/hooks/use-network-status';

import { getAppVersion, isAppVersionValid } from 'src/utils/guards/version-utils';

import { useSystemStore } from 'src/store/SystemStore';
import { SITE_ID, SiteIdEnum } from 'src/config-global';
import { useApplicationFlags } from 'src/store/AppFlags';
import { UpdatePolicyProvider } from 'src/providers/update-policy-provider';
import { getApiBaseUrlOverride } from 'src/store/SystemStore/api-base-url-override';

import { SplashScreen } from 'src/components/loading-screen';

import { AppNameEnum } from 'src/sections/app-hub/types';

import useAuthStore from '../store/firebase/auth-store';
import { getFirebaseWebApp } from '../store/firebase/lib';
import EnhancedMinimumVersionGuard from './minimum-version-guard-v2';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
  // eslint-disable-next-line react/no-unused-prop-types
  success?: boolean;
};
export const firebaseRemoteConfig = getRemoteConfig(getFirebaseWebApp());

firebaseRemoteConfig.defaultConfig = {
  minimum_app_version: '0.0.0',
  show_coming_soon_apps: false,
  apps_to_display_on_mobile: JSON.stringify(['Settings']),
  elevate_ai_enabled: false,
  enable_terminal_presence_wake_check: true,
  manager_v2_app_release: true,
  MOBILE_PAY_TAB_FEATURE: false,
  MANAGER_APP_USE_NATIVE_REGISTRATION: false,
  POINTCRAFT_MOBILE_APP_OVERRIDE: 'https://app.pointcraft.io',
  NOTIFICATIONS_FEAT_ENABLED: false,
  // Public Experiences MVP1 kill switch. Default false so the public page and
  // the admin tab fail closed when Remote Config is unavailable (PLAN.md D10).
  PUBLIC_EXPERIENCES_FEAT_ENABLED: false,
};

// For development only
firebaseRemoteConfig.settings = {
  minimumFetchIntervalMillis: 6000, //  6 seconds
  fetchTimeoutMillis: 30000,
};
// Feature flag to use the new Firestore-based update policy for version checking
// Note: Firebase Remote Config is still used for other app settings (base URLs, feature flags, etc.)
// This flag is now dynamically controlled based on authentication state to prevent login loops
const USE_FIRESTORE_UPDATE_POLICY = true; // Re-enabled with dynamic auth-based switching

function MinimumAppVersionContextWithRemoteConfig({ children }: Props) {
  // const { loading } = useAuthContext(); // Z CHANGE
  const [loading, setLoading] = React.useState(true);
  const [appVersionIsValid, setAppVersionIsValid] = React.useState(false);
  const [hasCheckedVersion, setHasCheckedVersion] = React.useState(false);
  const admittedPos = React.useRef(false);
  const isNative = Capacitor.isNativePlatform();
  const { setAllFlags } = useApplicationFlags();
  const { setBaseUrl, setMinimumAppVersion, setBaseAthUrl, setAppsToDisplayOnMobile, setFeatureFlags } =
    useSystemStore();
  const { isOnline } = useNetworkStatus();

  // Handle offline scenario
  const handleOfflineFlow = React.useCallback(async () => {
    console.log('Device is offline, skipping remote config fetch');
    try {
      // Get current app version without remote config
      const currentAppVersion = await getAppVersion();
      if (!currentAppVersion) {
        console.log('ERROR App Version result:', currentAppVersion);
        // When offline and can't get app version, allow through to avoid blocking
        setAppVersionIsValid(true);
        setLoading(false);
        return;
      }

      // When offline, use default values and allow through
      // We'll re-check when connection is restored
      setMinimumAppVersion(isNative ? firebaseRemoteConfig.defaultConfig.minimum_app_version as string : '0.0.0');
      setAppVersionIsValid(true); // Allow through when offline
      console.log('Offline mode: Allowing app to continue with version:', currentAppVersion);
    } catch (error) {
      console.error('Error in offline flow:', error);
      // Even if offline flow fails, allow through to avoid blocking
      setAppVersionIsValid(true);
    } finally {
      setLoading(false);
      setHasCheckedVersion(true);
    }
  }, [isNative, setMinimumAppVersion]);

  // Handle online version checking
  const checkVersionOnline = React.useCallback(async () => {
    console.log('Fetching remote config (online)');
    try {
      const activated = await fetchAndActivate(firebaseRemoteConfig);
      if (activated) {
        console.log('Remote config activated');
      } else {
        // check if values exist
        const allValues = await getAll(firebaseRemoteConfig);
        console.log('Current application Flags (Cloud):', allValues);
        setAllFlags({
          minimum_app_version: allValues.minimum_app_version.asString(),
          show_coming_soon_apps: allValues.show_coming_soon_apps?.asBoolean() || false,
          elevate_ai_enabled: allValues.elevate_ai_enabled?.asBoolean() || false,
          marketplace_v2_flag: allValues.marketplace_v2_flag?.asBoolean() || false,
          manager_v2_app_release: allValues.manager_v2_app_release?.asBoolean() ?? true,
          mobile_pay_tab_feature: allValues.MOBILE_PAY_TAB_FEATURE?.asBoolean() ?? false,
          enable_terminal_presence_wake_check:
            allValues.enable_terminal_presence_wake_check?.asBoolean() ?? true,
        });
        // A debug base-URL override (Config & API tab) always wins over remote config.
        if (allValues?.custom_base_url?.asString() && !getApiBaseUrlOverride()) {
          setBaseUrl(allValues.custom_base_url.asString());
        }
        if (allValues?.custom_base_ath_url?.asString()) {
          setBaseAthUrl(allValues.custom_base_ath_url.asString());
        }
        // elevate ai
        if (allValues?.elevate_ai_enabled?.asBoolean()) {
          setFeatureFlags({ elevateAiEnabled: allValues.elevate_ai_enabled.asBoolean() });
        }
      }
      // base url
      const baseUrl = getValue(firebaseRemoteConfig, 'custom_base_url');
      const baseAthUrl = getValue(firebaseRemoteConfig, 'custom_base_ath_url');
      const appsToDisplayOnMobile = getValue(firebaseRemoteConfig, 'apps_to_display_on_mobile');
      if (baseUrl.asString() && !getApiBaseUrlOverride()) {
        setBaseUrl(baseUrl.asString());
      }
      if (baseAthUrl.asString()) {
        setBaseAthUrl(baseAthUrl.asString());
      }
      if (appsToDisplayOnMobile.asString()) {
        try {
          setAppsToDisplayOnMobile(JSON.parse(appsToDisplayOnMobile.asString()) as AppNameEnum[]);
        } catch (error) {
          console.error('Error parsing apps to display on mobile:', error);
        }
      }
      const minimumAppVersion = getValue(firebaseRemoteConfig, 'minimum_app_version');
      console.log('Minimum App Version:', minimumAppVersion.asString());
      const currentAppVersion = await getAppVersion();
      if (!currentAppVersion) {
        console.log('ERROR App Version result:', currentAppVersion);
        throw new Error('App Version is not valid');
      }
      const isValid = !isNative || isAppVersionValid(currentAppVersion, minimumAppVersion.asString());

      console.log('Current App Version:', currentAppVersion);
      console.log('Minimum App Version:', minimumAppVersion.asString());
      console.log('Is Valid:', isValid);
      setMinimumAppVersion(isNative ? minimumAppVersion.asString() : '0.0.0');
      setAppVersionIsValid(isValid);
    } catch (error) {
      console.error('Error getting app version:', error);
      // A failed runtime fetch is not a new version-policy verdict.
      if (!admittedPos.current) setAppVersionIsValid(!isNative);
    } finally {
      setLoading(false);
      setHasCheckedVersion(true);
    }
  }, [
    isNative,
    setAllFlags,
    setMinimumAppVersion,
    setBaseUrl,
    setBaseAthUrl,
    setAppsToDisplayOnMobile,
    setFeatureFlags,
  ]);

  // Initial version check effect
  React.useEffect(() => {
    if (!hasCheckedVersion) {
      if (isOnline) {
        checkVersionOnline();
      } else {
        handleOfflineFlow();
      }
    }
  }, [isOnline, hasCheckedVersion, checkVersionOnline, handleOfflineFlow]);

  // Handle reconnection - re-check version when coming back online
  React.useEffect(() => {
    if (isOnline && hasCheckedVersion) {
      console.log('Connection restored, re-checking app version');
      setLoading(true);
      checkVersionOnline();
    }
  }, [isOnline, hasCheckedVersion, checkVersionOnline]);
  if (!loading && appVersionIsValid && SITE_ID === SiteIdEnum.PointOfSale) {
    admittedPos.current = true;
  }
  if (!isNative) return <>{children}</>;

  return (
    <>
      {loading && !admittedPos.current ? (
        <SplashScreen />
      ) : (
        <Container success={appVersionIsValid}>{children}</Container>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function Container({ success, children }: Props) {
  const router = useRouter();

  const check = useCallback(() => {
    if (!success) {
      router.replace(paths.system.updateVersion);
    }
  }, [success, router]);

  useEffect(() => {
    check();
  }, [check]);

  return <>{children}</>;
}

// Combined implementation that uses both Firebase Remote Config and Firestore
function CombinedMinimumAppVersionContext({ children }: Props) {
  const [remoteConfigLoaded, setRemoteConfigLoaded] = React.useState(false);
  const { isOnline } = useNetworkStatus();
  const { setAllFlags } = useApplicationFlags();
  const { setBaseUrl, setBaseAthUrl, setAppsToDisplayOnMobile, setFeatureFlags } = useSystemStore();

  // Load Firebase Remote Config for app settings (not version checking)
  const loadRemoteConfig = React.useCallback(async () => {
    console.warn('[CombinedMinimumAppVersionContext] loadRemoteConfig called, isOnline:', isOnline);
    if (!isOnline) {
      console.warn('[CombinedMinimumAppVersionContext] Offline: Skipping remote config fetch');
      setRemoteConfigLoaded(true);
      return;
    }

    try {
      console.log('Fetching remote config for app settings');
      const activated = await fetchAndActivate(firebaseRemoteConfig);

      if (activated) {
        console.log('Remote config activated');
      }

      const allValues = await getAll(firebaseRemoteConfig);

      // Set application flags (excluding version checking)
      setAllFlags({
        minimum_app_version: allValues.minimum_app_version.asString(), // Keep for backward compatibility
        show_coming_soon_apps: allValues.show_coming_soon_apps?.asBoolean() || false,
        elevate_ai_enabled: allValues.elevate_ai_enabled?.asBoolean() || false,
        marketplace_v2_flag: allValues.marketplace_v2_flag?.asBoolean() || false,
        manager_v2_app_release: allValues.manager_v2_app_release?.asBoolean() ?? true,
        mobile_pay_tab_feature: allValues.MOBILE_PAY_TAB_FEATURE?.asBoolean() ?? false,
        enable_terminal_presence_wake_check:
          allValues.enable_terminal_presence_wake_check?.asBoolean() ?? true,
      });

      // Set base URLs
      const baseUrl = getValue(firebaseRemoteConfig, 'custom_base_url');
      const baseAthUrl = getValue(firebaseRemoteConfig, 'custom_base_ath_url');
      const appsToDisplayOnMobile = getValue(firebaseRemoteConfig, 'apps_to_display_on_mobile');

      if (baseUrl.asString() && !getApiBaseUrlOverride()) {
        setBaseUrl(baseUrl.asString());
      }
      if (baseAthUrl.asString()) {
        setBaseAthUrl(baseAthUrl.asString());
      }
      if (appsToDisplayOnMobile.asString()) {
        try {
          setAppsToDisplayOnMobile(JSON.parse(appsToDisplayOnMobile.asString()) as AppNameEnum[]);
        } catch (error) {
          console.error('Error parsing apps to display on mobile:', error);
        }
      }
      if (allValues?.elevate_ai_enabled?.asBoolean()) {
        setFeatureFlags({ elevateAiEnabled: allValues.elevate_ai_enabled.asBoolean() });
      }
    } catch (error) {
      console.error('[CombinedMinimumAppVersionContext] Error fetching remote config:', error);
    } finally {
      setRemoteConfigLoaded(true);
    }
  }, [isOnline, setAllFlags, setBaseUrl, setBaseAthUrl, setAppsToDisplayOnMobile, setFeatureFlags]);

  // Load remote config on mount and when online status changes
  React.useEffect(() => {
    loadRemoteConfig();
  }, [loadRemoteConfig]);

  if (!remoteConfigLoaded) {
    return <SplashScreen />;
  }

  // Once remote config is loaded, use the Firestore-based version checking
  return (
    <UpdatePolicyProvider>
      <EnhancedMinimumVersionGuard>{children}</EnhancedMinimumVersionGuard>
    </UpdatePolicyProvider>
  );
}

// Export the appropriate version based on feature flag AND authentication state
export default function MinimumAppVersionContext({ children }: Props) {
  const { authenticated, loading: authLoading } = useAuthStore();
  const [authStateChecked, setAuthStateChecked] = React.useState(false);

  // Track when auth state has been determined (either authenticated or not)
  React.useEffect(() => {
    if (!authLoading) {
      setAuthStateChecked(true);
    }
  }, [authLoading]);

  // Wait for auth state to be determined before deciding which implementation to use
  // This prevents the component from switching implementations mid-lifecycle
  if (!authStateChecked) {
    return <SplashScreen />;
  }

  // Dynamic switching based on authentication state
  // Use Firestore-based policy only when authenticated AND feature flag is enabled
  if (USE_FIRESTORE_UPDATE_POLICY && authenticated && Capacitor.isNativePlatform()) {
    // Combined implementation: Firebase Remote Config for settings + Firestore for version policy
    return <CombinedMinimumAppVersionContext>{children}</CombinedMinimumAppVersionContext>;
  }

  // Legacy implementation with Firebase Remote Config for everything
  // Used for: pre-auth scenarios, web platform, or when feature flag is disabled
  return (
    <MinimumAppVersionContextWithRemoteConfig>{children}</MinimumAppVersionContextWithRemoteConfig>
  );
}
